import Checkbox from '@ingka/checkbox';
import FormField from '@ingka/form-field';
import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useLocale, useStores } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { BaseLotteryInput } from '@lotta/self-service/lottery-fe/types-form';
import type { Control, Path, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import RadioButtonGroup from '@ingka/radio-button-group';
import InlineMessage from '@ingka/inline-message';
import styles from './ControlledStoresCheckboxes.module.scss';

type Values = Pick<BaseLotteryInput, 'storeIds'>;

export type ControlledStoresCheckboxesProps<TFormData extends Values = Values> = {
  control: Control<TFormData>;
  setValue: (
    name: Path<TFormData>,
    value: TFormData['storeIds'],
    options?: Parameters<UseFormSetValue<TFormData>>[2]
  ) => void;
  getValues: () => TFormData;
  errorMessage: string | undefined;
};

export const ControlledStoresCheckboxes = <TFormData extends Values = Values>({
  errorMessage,
  control,
  setValue,
  getValues,
}: ControlledStoresCheckboxesProps<TFormData>) => {
  const { t } = useTranslation();
  const { retailUnit, language } = useLocale();
  const { data: retailUnitData, isLoading: retailUnitIsLoading } = useGetRetailUnitQuery({
    retailUnit,
  });
  const { data, isLoading } = useStores({ retailUnit, language, retailUnitData });

  const availableStores = useMemo(() => (data && data.stores ? data.stores : []), [data]);

  const storeIds: string[] | undefined = getValues()['storeIds'];

  const [storeConstraint, setStoreConstraint] = useState(
    storeIds && storeIds.length > 0 && storeIds.length < availableStores.length ? 'show' : 'hide'
  ); // 'show' or 'hide'

  if (isLoading || retailUnitIsLoading) {
    return (
      <Loading text="" data-testid="loading-ball">
        <LoadingBall />
      </Loading>
    );
  }

  if (!availableStores.length) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  if (errorMessage) {
    errorMessage = `${t(errorMessage)}`;
  }

  const RadioButtonsArray = [
    {
      // eslint-disable-next-line i18next/no-literal-string
      id: 'allStoresRadio',
      label: t('lottery-form-store-open-to-all-stores'),
      // eslint-disable-next-line i18next/no-literal-string
      value: 'hide',
      disabled: false,
    },
    {
      // eslint-disable-next-line i18next/no-literal-string
      id: 'specificStoresRadio',
      label: t('lottery-form-store-limit-to-specific-stores'),
      // eslint-disable-next-line i18next/no-literal-string
      value: 'show',
      disabled: false,
    },
  ];

  return (
    <FormField
      shouldValidate={!!errorMessage}
      valid={!errorMessage}
      validation={{
        msg: errorMessage,
        type: 'error',
      }}
    >
      <Controller
        name={'storeIds' as Path<TFormData>}
        control={control}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <>
            <Text className={styles['text']}>
              <b>{t('lottery-form-store-limit-stores-title')}</b>
            </Text>
            <Text className={styles['text']}>
              {t('lottery-form-store-limit-stores-description')}
            </Text>
            {/* Step 2: Store List Visibility Radio Button Group */}
            <RadioButtonGroup
              list={RadioButtonsArray.map((radioButton) => ({
                ...radioButton,
                checked: storeConstraint === radioButton.value,
                onBlur,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setStoreConstraint(e.currentTarget.value);
                  if (e.currentTarget.value === 'hide') {
                    setValue('storeIds' as Path<TFormData>, undefined, {
                      shouldValidate: true,
                    });
                  } else {
                    setValue('storeIds' as Path<TFormData>, [], { shouldValidate: true });
                  }
                },
              }))}
            />
            {storeConstraint === 'hide' && (
              <>
                <Text className={styles['text']}>
                  <b>{t('lottery-form-store-limit-winner-title')}</b>
                </Text>
                <InlineMessage
                  className={styles['inline']}
                  body={t('lottery-form-store-warning-message')}
                  variant="cautionary"
                />
                <Checkbox
                  id="all-stores"
                  value="all-stores"
                  label={t('lottery-form-store-limit-winner-lable')}
                  ref={ref}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.currentTarget.checked
                      ? setValue(
                          'storeIds' as Path<TFormData>,
                          availableStores.map(({ id }) => id, { shouldValidate: true })
                        )
                      : setValue('storeIds' as Path<TFormData>, undefined, {
                          shouldValidate: true,
                        });
                  }}
                  onBlur={onBlur}
                  indeterminate={
                    Array.isArray(value) &&
                    value.length > 0 &&
                    value.length < availableStores.length
                  }
                  checked={getValues()['storeIds'] !== undefined}
                />
              </>
            )}
            {storeConstraint === 'show' && (
              <>
                <Text className={styles['text']}>
                  <b>{t('lottery-form-store-header')}</b>
                </Text>
                <ul className={styles['store-checkbox-list']}>
                  {availableStores.map((store) => {
                    return (
                      <li key={store.id}>
                        <Checkbox
                          className={styles['store-checkbox']}
                          id={store.id}
                          value={store.displayName}
                          name={store.displayName}
                          label={store.displayName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (Array.isArray(value)) {
                              const valuesWithoutThisId = value.filter(
                                (storeId) => storeId !== store.id
                              );
                              if (e.currentTarget.checked) {
                                onChange([...valuesWithoutThisId, store.id]);
                              } else {
                                onChange(valuesWithoutThisId);
                              }
                            }
                          }}
                          onBlur={onBlur}
                          checked={value?.includes(store.id)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      />
    </FormField>
  );
};
